import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Eye, Save } from 'lucide-react';
import LoaderOverlay from '../common/LoaderOverlay';
import SourceCombobox from '../common/SourceCombobox';

export interface PrivacySettings {
  profileVisibility: 'public' | 'private';
  showEmail: boolean;
  showPhone: boolean;
  showTradingStats: boolean;
  allowMessages: boolean;
}

interface ProfileVisibilityProps {
  privacy: PrivacySettings;
  onInputChange: (field: string, value: string | boolean) => void;
  onSave: () => void;
  isSaving: boolean;
}

const visibilityOptions = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
] as const;

const ProfileVisibility = ({
  privacy,
  onInputChange,
  onSave,
  isSaving,
}: ProfileVisibilityProps) => {
  return (
    <LoaderOverlay isLoading={isSaving} loadingText="Saving...">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
            <Eye className="h-5 w-5 flex-shrink-0" />
            <span>Profile Visibility</span>
          </CardTitle>
          <CardDescription>
            Control who can see your profile information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 min-w-0 flex-1 mr-2">
                <Label className="text-sm">Profile Visibility</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Make your profile public or private
                </p>
              </div>
              <div className="w-32">
                <SourceCombobox
                  items={visibilityOptions.map(option => option.label)}
                  placeholder="Select visibility"
                  value={
                    visibilityOptions.find(
                      option => option.value === privacy.profileVisibility
                    )?.label || ''
                  }
                  onChange={value => {
                    const option = visibilityOptions.find(
                      item => item.label === value
                    );
                    if (option) {
                      onInputChange('profileVisibility', option.value);
                    }
                  }}
                  emptyText="No options found."
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 min-w-0 flex-1 mr-2">
                <Label className="text-sm">Show Email</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Display email on your profile
                </p>
              </div>
              <Button
                variant={privacy.showEmail ? 'default' : 'outline'}
                size="sm"
                onClick={() => onInputChange('showEmail', !privacy.showEmail)}
              >
                {privacy.showEmail ? 'Visible' : 'Hidden'}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 min-w-0 flex-1 mr-2">
                <Label className="text-sm">Show Phone</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Display phone number on your profile
                </p>
              </div>
              <Button
                variant={privacy.showPhone ? 'default' : 'outline'}
                size="sm"
                onClick={() => onInputChange('showPhone', !privacy.showPhone)}
              >
                {privacy.showPhone ? 'Visible' : 'Hidden'}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 min-w-0 flex-1 mr-2">
                <Label className="text-sm">Show Trading Stats</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Display your trading performance
                </p>
              </div>
              <Button
                variant={privacy.showTradingStats ? 'default' : 'outline'}
                size="sm"
                onClick={() =>
                  onInputChange('showTradingStats', !privacy.showTradingStats)
                }
              >
                {privacy.showTradingStats ? 'Visible' : 'Hidden'}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 min-w-0 flex-1 mr-2">
                <Label className="text-sm">Allow Messages</Label>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Let other users send you messages
                </p>
              </div>
              <Button
                variant={privacy.allowMessages ? 'default' : 'outline'}
                size="sm"
                onClick={() =>
                  onInputChange('allowMessages', !privacy.allowMessages)
                }
              >
                {privacy.allowMessages ? 'Allowed' : 'Blocked'}
              </Button>
            </div>
          </div>
          <div className="flex justify-center sm:justify-end pt-4">
            <Button onClick={onSave} disabled={isSaving}>
              <div className="flex items-center space-x-2">
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </LoaderOverlay>
  );
};

export default ProfileVisibility;
